import { formatCurrencyString } from "../../../utils/format"
import { LineItem, Subscription, SubscriptionData } from "./subscription"

export interface FeatureBilling {
  productName: string
  currentPeriodValue?: number
  currentPeriodLimit?: number
  pricePerUnit: number
  numPeriodUpfront: number
  upfrontPeriodsRemaining: number
  numPeriodNoLimit: number
  noLimitPeriodsRemaining: number
  priceId?: string
  secondaryPriceId?: string
}
export interface MembershipProps {
  status: "active" | "inactive" | "trialing"
  endDate: {
    formatted: string
  }
  subscription: SubscriptionData
  featureBilling: FeatureBilling[]
}

export class Membership {
  status: "active" | "inactive" | "trialing"
  endDate: {
    formatted: string
  }
  subscription: Subscription
  featureBilling: FeatureBilling[]

  constructor(props: MembershipProps) {
    this.endDate = props.endDate
    this.status = props.status
    this.subscription = new Subscription(props.subscription)
    this.featureBilling = props.featureBilling
  }

  isProfesssional() {
    return this.subscription.isProfesssional()
  }

  isPersonal() {
    return this.subscription.isPersonal()
  }

  getPriceQuanty(lineItem?: LineItem): number {
    if (!lineItem?.priceId) {
      return lineItem?.quantity ?? 0
    }

    const isPrimary = this.isPrimaryFeature(lineItem.priceId)

    const feature = isPrimary
      ? this.getPrimaryFeatureUsage(lineItem.priceId)
      : this.getSecondaryFeatureUsage(lineItem.priceId)

    if (!feature) {
      return lineItem?.quantity ?? 0
    }

    if (feature.noLimitPeriodsRemaining > 0) {
      return 0
    }
    if (isPrimary && this.subscription.cancelAtPeriodEnd) {
      return 0
    }
    return feature.billed
  }

  isPrimaryFeature(priceId?: string): boolean {
    if (!this.featureBilling || this.featureBilling.length <= 0) {
      return false
    }
    return this.featureBilling.some(f => f.priceId === priceId)
  }

  isSecondaryFeature(priceId?: string): boolean {
    if (!this.featureBilling || this.featureBilling.length <= 0) {
      return false
    }
    return this.featureBilling.some(f => f.secondaryPriceId === priceId)
  }

  getPrimaryFeatureUsage(
    priceId?: string
  ): {
    included: number
    used: number
    remaining: number
    billed: number
    noLimitPeriodsRemaining: number
  } {
    const res = {
      included: 0,
      used: 0,
      remaining: 0,
      billed: 0,
      noLimitPeriodsRemaining: 0,
    }
    if (!this.featureBilling || this.featureBilling.length <= 0) {
      return res
    }

    const featurePrice = this.featureBilling.find(f => f.priceId === priceId)
    if (featurePrice) {
      res.noLimitPeriodsRemaining = featurePrice.upfrontPeriodsRemaining ?? 0
      res.remaining = res.noLimitPeriodsRemaining
      res.included = featurePrice.numPeriodUpfront ?? 0
      res.used = 1

      res.billed = res.remaining === 0 ? 1 : 0
    }

    return res
  }

  getSecondaryFeatureUsage(
    priceId?: string
  ): {
    included: number
    used: number
    remaining: number
    billed: number
    noLimitPeriodsRemaining: number
  } {
    const res = {
      included: 0,
      used: 0,
      remaining: 0,
      billed: 0,
      noLimitPeriodsRemaining: 0,
    }
    if (!this.featureBilling || this.featureBilling.length <= 0) {
      return res
    }

    const featurePrice = this.featureBilling.find(
      f => f.secondaryPriceId === priceId
    )
    if (featurePrice) {
      res.noLimitPeriodsRemaining = featurePrice.noLimitPeriodsRemaining ?? 0
      res.included = featurePrice.currentPeriodLimit ?? 0
      res.used = featurePrice.currentPeriodValue ?? 0
      res.remaining = Math.max(res.included - res.used, 0)
      res.billed =
        res.used > 0 && res.remaining === 0 ? res.used - res.included : 0
    }

    return res
  }

  buildLineItemDescription(lineItem: LineItem): string {
    return `${lineItem.description}`
  }
  buildLineItemSubDescription(lineItem: LineItem): string {
    if (this.isPrimaryFeature(lineItem.priceId)) {
      const featureUsage = this.getPrimaryFeatureUsage(lineItem.priceId)

      return featureUsage.noLimitPeriodsRemaining > 0
        ? `${formatCurrencyString({
            value: lineItem.unitAmount,
            currency: lineItem.currency.toUpperCase(),
          })} pre-paid`
        : this.subscription.cancelAtPeriodEnd
        ? "Canceled"
        : ""
    }

    if (this.isSecondaryFeature(lineItem.priceId)) {
      const featureUsage = this.getSecondaryFeatureUsage(lineItem.priceId)
      return featureUsage.noLimitPeriodsRemaining > 0
        ? `${featureUsage.used} completed (unlimited use this billling cycle)`
        : featureUsage.remaining > 0
        ? `${featureUsage.remaining} remaining of ${featureUsage.included} included in plan`
        : featureUsage.included > 0
        ? `${featureUsage.billed} completed exceeding ${featureUsage.included} included in plan`
        : `${featureUsage.used} completed`
    }
    return `${lineItem.description}`
  }
}
